
import React, { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { DashboardHeader } from "@/components/admin/DashboardHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Bell, BellDot, MessageSquare, Users, Mail, Info, Settings, Send } from "lucide-react";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// Typedefinitionen für Benachrichtigungen
interface Notification {
  id: string;
  title: string;
  message: string;
  type: "info" | "success" | "warning" | "error";
  date: string;
  read: boolean;
}

const Notifications = () => {
  const [activeTab, setActiveTab] = useState<"inbox" | "settings" | "send">("inbox");
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: "1",
      title: "Neuer Benutzer registriert",
      message: "Ein neuer Benutzer hat sich auf der Plattform registriert.",
      type: "info",
      date: "Heute, 10:23",
      read: false
    },
    {
      id: "2",
      title: "Systemwartung geplant",
      message: "Am Sonntag, den 20. Oktober, wird das System zwischen 2:00 und 4:00 Uhr für Wartungsarbeiten nicht verfügbar sein.",
      type: "warning",
      date: "Gestern, 15:45",
      read: true
    },
    {
      id: "3",
      title: "Neue Datei hochgeladen",
      message: "Es wurde eine neue Datei im Dateimanager hochgeladen: Immobilienbewertung.pdf",
      type: "success",
      date: "15. Okt, 09:12",
      read: true
    }
  ]);
  
  const [emailSettings, setEmailSettings] = useState({
    newUser: true,
    systemUpdates: true,
    fileUploads: false,
    supportRequests: true,
    dailyDigest: false
  });
  
  const [pushSettings, setPushSettings] = useState({
    newUser: true,
    systemUpdates: true,
    fileUploads: true,
    supportRequests: true,
    dailyDigest: false
  });
  
  const [newNotification, setNewNotification] = useState({
    title: "",
    message: "",
    recipients: "all" // all, admins, users
  });

  const markAsRead = (id: string) => {
    setNotifications(
      notifications.map(notification => 
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const handleMarkAllAsRead = () => {
    setNotifications(
      notifications.map(notification => ({ ...notification, read: true }))
    );
    toast.success("Alle Benachrichtigungen als gelesen markiert");
  };

  const handleDeleteNotification = (id: string) => {
    setNotifications(notifications.filter(notification => notification.id !== id));
    toast.success("Benachrichtigung gelöscht");
  };

  const handleDeleteAll = () => {
    setNotifications([]);
    toast.success("Alle Benachrichtigungen gelöscht");
  };

  const handleEmailSettingChange = (setting: keyof typeof emailSettings) => {
    setEmailSettings({
      ...emailSettings,
      [setting]: !emailSettings[setting]
    });
  };

  const handlePushSettingChange = (setting: keyof typeof pushSettings) => {
    setPushSettings({
      ...pushSettings,
      [setting]: !pushSettings[setting]
    });
  };

  const handleSendNotification = () => {
    if (!newNotification.title || !newNotification.message) {
      toast.error("Bitte Titel und Nachricht eingeben");
      return;
    }

    // Hier würde in einem echten System die Nachricht versendet werden
    toast.success(`Benachrichtigung an ${newNotification.recipients === "all" ? "alle Benutzer" : 
      newNotification.recipients === "admins" ? "alle Administratoren" : "alle regulären Benutzer"} gesendet`);
    
    setNewNotification({
      title: "",
      message: "",
      recipients: "all"
    });
  };

  return (
    <AdminLayout>
      <div className="flex flex-col w-full h-full">
        <DashboardHeader 
          userName="Admin" 
          fullName="System Administrator" 
          email="admin@hoss-immo.de" 
        />
        
        <div className="p-6">
          <h2 className="text-2xl font-semibold mb-6">Benachrichtigungen</h2>
          
          <Tabs 
            value={activeTab} 
            onValueChange={(value) => setActiveTab(value as "inbox" | "settings" | "send")} 
            className="w-full"
          >
            <TabsList className="mb-6">
              <TabsTrigger value="inbox">
                <Bell className="mr-2 h-4 w-4" />
                Posteingang
              </TabsTrigger>
              <TabsTrigger value="send">
                <Send className="mr-2 h-4 w-4" />
                Benachrichtigung senden
              </TabsTrigger>
              <TabsTrigger value="settings">
                <Settings className="mr-2 h-4 w-4" />
                Einstellungen
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="inbox" className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <span className="text-sm text-gray-400">
                    {notifications.filter(n => !n.read).length} ungelesene Benachrichtigungen
                  </span>
                </div>
                <div className="space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleMarkAllAsRead}
                    disabled={!notifications.some(n => !n.read)}
                  >
                    Alle als gelesen markieren
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDeleteAll}
                    disabled={notifications.length === 0}
                  >
                    Alle löschen
                  </Button>
                </div>
              </div>
              
              {notifications.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Bell className="mx-auto h-12 w-12 mb-4 opacity-30" />
                  <p>Keine Benachrichtigungen vorhanden</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notifications.map((notification) => (
                    <Card 
                      key={notification.id}
                      className={`transition-all ${!notification.read ? 'border-l-4 border-l-[#41c7f0]' : ''}`}
                    >
                      <CardHeader className="py-4 flex flex-row items-start justify-between space-y-0">
                        <div className="flex gap-4 items-center">
                          {!notification.read && <BellDot className="h-5 w-5 text-[#41c7f0]" />}
                          {notification.read && <Bell className="h-5 w-5 text-gray-400" />}
                          <div>
                            <CardTitle className="text-base font-medium">
                              {notification.title}
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {notification.date}
                            </CardDescription>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {!notification.read && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => markAsRead(notification.id)}
                            >
                              Als gelesen markieren
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteNotification(notification.id)}
                          >
                            Löschen
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="py-2">
                        <p className="text-sm">{notification.message}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="settings">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      <Mail className="inline-block mr-2 h-5 w-5" />
                      Email-Benachrichtigungen
                    </CardTitle>
                    <CardDescription>
                      Verwalten Sie, welche Emails Sie erhalten möchten
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Neue Benutzer</Label>
                        <p className="text-xs text-gray-400">
                          Benachrichtigungen, wenn sich neue Benutzer registrieren
                        </p>
                      </div>
                      <Switch 
                        checked={emailSettings.newUser}
                        onCheckedChange={() => handleEmailSettingChange("newUser")}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>System-Updates</Label>
                        <p className="text-xs text-gray-400">
                          Benachrichtigungen über Systemwartungen und Updates
                        </p>
                      </div>
                      <Switch 
                        checked={emailSettings.systemUpdates}
                        onCheckedChange={() => handleEmailSettingChange("systemUpdates")}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Datei-Uploads</Label>
                        <p className="text-xs text-gray-400">
                          Benachrichtigungen, wenn neue Dateien hochgeladen werden
                        </p>
                      </div>
                      <Switch 
                        checked={emailSettings.fileUploads}
                        onCheckedChange={() => handleEmailSettingChange("fileUploads")}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Support-Anfragen</Label>
                        <p className="text-xs text-gray-400">
                          Benachrichtigungen bei neuen Support-Anfragen
                        </p>
                      </div>
                      <Switch 
                        checked={emailSettings.supportRequests}
                        onCheckedChange={() => handleEmailSettingChange("supportRequests")}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Tägliche Zusammenfassung</Label>
                        <p className="text-xs text-gray-400">
                          Tägliche Zusammenfassung aller Aktivitäten
                        </p>
                      </div>
                      <Switch 
                        checked={emailSettings.dailyDigest}
                        onCheckedChange={() => handleEmailSettingChange("dailyDigest")}
                      />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      <BellDot className="inline-block mr-2 h-5 w-5" />
                      Push-Benachrichtigungen
                    </CardTitle>
                    <CardDescription>
                      Verwalten Sie Push-Benachrichtigungen im Browser
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Neue Benutzer</Label>
                        <p className="text-xs text-gray-400">
                          Benachrichtigungen, wenn sich neue Benutzer registrieren
                        </p>
                      </div>
                      <Switch 
                        checked={pushSettings.newUser}
                        onCheckedChange={() => handlePushSettingChange("newUser")}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>System-Updates</Label>
                        <p className="text-xs text-gray-400">
                          Benachrichtigungen über Systemwartungen und Updates
                        </p>
                      </div>
                      <Switch 
                        checked={pushSettings.systemUpdates}
                        onCheckedChange={() => handlePushSettingChange("systemUpdates")}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Datei-Uploads</Label>
                        <p className="text-xs text-gray-400">
                          Benachrichtigungen, wenn neue Dateien hochgeladen werden
                        </p>
                      </div>
                      <Switch 
                        checked={pushSettings.fileUploads}
                        onCheckedChange={() => handlePushSettingChange("fileUploads")}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Support-Anfragen</Label>
                        <p className="text-xs text-gray-400">
                          Benachrichtigungen bei neuen Support-Anfragen
                        </p>
                      </div>
                      <Switch 
                        checked={pushSettings.supportRequests}
                        onCheckedChange={() => handlePushSettingChange("supportRequests")}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Tägliche Zusammenfassung</Label>
                        <p className="text-xs text-gray-400">
                          Tägliche Zusammenfassung aller Aktivitäten
                        </p>
                      </div>
                      <Switch 
                        checked={pushSettings.dailyDigest}
                        onCheckedChange={() => handlePushSettingChange("dailyDigest")}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="send">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    <Send className="inline-block mr-2 h-5 w-5" />
                    Benachrichtigung senden
                  </CardTitle>
                  <CardDescription>
                    Senden Sie eine Benachrichtigung an Benutzer der Plattform
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Titel</Label>
                    <Input
                      id="title"
                      placeholder="Titel der Benachrichtigung"
                      value={newNotification.title}
                      onChange={(e) => setNewNotification({ ...newNotification, title: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Nachricht</Label>
                    <Textarea
                      id="message"
                      placeholder="Nachricht eingeben..."
                      className="min-h-32"
                      value={newNotification.message}
                      onChange={(e) => setNewNotification({ ...newNotification, message: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="recipients">Empfänger</Label>
                    <select
                      id="recipients"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                      value={newNotification.recipients}
                      onChange={(e) => setNewNotification({ ...newNotification, recipients: e.target.value })}
                    >
                      <option value="all">Alle Benutzer</option>
                      <option value="admins">Nur Administratoren</option>
                      <option value="users">Nur reguläre Benutzer</option>
                    </select>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button onClick={handleSendNotification}>
                    <Send className="mr-2 h-4 w-4" />
                    Benachrichtigung senden
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Notifications;
